import React, { useState, useMemo, useRef } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import classnames from 'classnames';
import SkyPulseTable from 'components/SkyPulseTable';
import EventTypeFilterOverlay from 'components/overlays/EventTypeFilterOverlay';
import { FILTER_EVENT_TYPE_OPTIONS } from 'constants';
import styles from './styles.module.css';
import ButtonList from 'view/components/inputs/ButtonList';
import PropTypes from 'prop-types';

const Events = (props) => {
  const {
    rowData,
    eventFilterOptions,
    defaultFilter,
    onRowClick,
    columnDefinitions,
    filterTypeConfig,
    loading,
    idPrefix,
  } = props;

  const [selectedEventFilter, setSelectedEventFilter] = useState(
    defaultFilter || eventFilterOptions[0],
  );
  const [currentEventsFilter, setCurrentEventsFilter] = useState(
    filterTypeConfig[defaultFilter?.id || eventFilterOptions[0]?.id] || {},
  );
  const [eventFilterOverlayCoordinates, setEventFilterOverlayCoordinates] =
    useState(null);
  const [disabled, setDisabled] = useState(false);

  const eventFilterButtonRef = useRef();
  const pendingEventFilterRef = useRef(null);

  const filteredRowData = useMemo(() => {
    if (!rowData) return [];
    return rowData.filter((row) => currentEventsFilter[row.eventTypeId]);
  }, [rowData, currentEventsFilter]);

  const onEventFilterPickerOpen = (ref) => {
    setDisabled(true);
    const { right, bottom } = ref.current.getBoundingClientRect();
    setEventFilterOverlayCoordinates({ x: right - 700, y: bottom });
  };

  const EventFilterOptions = useMemo(() => {
    return eventFilterOptions.map((option) => {
      if (option.hasOptions) {
        return {
          name: (
            <div>
              {option.name}
              <Button
                data-cy-id="LruDetailsOverlay-eventsFilterButton"
                ref={eventFilterButtonRef}
                onClick={() => {
                  setSelectedEventFilter(option);
                  onEventFilterPickerOpen(eventFilterButtonRef);
                }}
                disabled={disabled}
                className={classnames(
                  'button_reg',
                  'ml-2',
                  styles.eventsFilterButton,
                )}
              >
                {selectedEventFilter?.hasOptions
                  ? (FILTER_EVENT_TYPE_OPTIONS.filter(
                      (item) => currentEventsFilter?.[item.value],
                    ).length || 'none') + ' selected'
                  : '--'}
              </Button>
            </div>
          ),
          value: option.id,
          hasOptions: option.hasOptions,
        };
      }
      return {
        name: option.name,
        value: option.id,
        hasOptions: option.hasOptions,
      };
    });
  }, [eventFilterOptions, disabled, selectedEventFilter, currentEventsFilter]);

  const onEventFilterChange = ({ target: { value } }) => {
    const selectedOption = eventFilterOptions.find(
      (option) => option.id === value,
    );
    if (selectedOption.hasOptions) {
      pendingEventFilterRef.current = selectedOption;
      onEventFilterPickerOpen(eventFilterButtonRef);
    } else {
      setSelectedEventFilter(selectedOption);
      setCurrentEventsFilter(filterTypeConfig[value]);
    }
  };

  const onEventsFilterCancel = () => {
    setEventFilterOverlayCoordinates(null);
    setDisabled(false);
    pendingEventFilterRef.current = null;
  };

  const onEventsFilterDone = (filters) => {
    const newFilters = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        newFilters[key] = true;
      }
    });
    setSelectedEventFilter(pendingEventFilterRef.current);
    setCurrentEventsFilter(newFilters);
    setEventFilterOverlayCoordinates(null);
    setDisabled(false);
    pendingEventFilterRef.current = null;
  };

  return (
    <>
      <Row
        data-cy-id="LruDetailsOverlay-eventsContainer"
        className={styles.eventContainer}
        noGutters
      >
        <Col className="h-100">
          <Row className={styles.tableTitle} noGutters>
            <Col
              data-cy-id="LruDetailsOverlay-eventsTableTitle"
              className={styles.title}
            >
              {' '}
              Events - Last 30 days{' '}
            </Col>
            <Col md="auto">
              <ButtonList
                idPrefix={`${idPrefix}-Events-filter`}
                name="eventFilters"
                type="radio"
                onChange={onEventFilterChange}
                options={EventFilterOptions}
                value={selectedEventFilter?.id}
                arrangementClassName={styles.filterList}
                disabled={disabled}
                classname={styles.options}
                testIdPrefix="LruDetailsOverlay"
              />
            </Col>
          </Row>
          <Row className={styles.eventsTable} noGutters>
            <Col className="h-100">
              <SkyPulseTable
                columns={columnDefinitions}
                rows={filteredRowData}
                onRowClick={onRowClick}
                defaultSort="utc"
                isLoading={loading}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <EventTypeFilterOverlay
        position={eventFilterOverlayCoordinates}
        isOpen={eventFilterOverlayCoordinates !== null}
        onCancel={onEventsFilterCancel}
        onDone={onEventsFilterDone}
        filters={currentEventsFilter}
      />
    </>
  );
};

Events.propTypes = {
  rowData: PropTypes.array,
  eventFilterOptions: PropTypes.array.isRequired,
  defaultFilter: PropTypes.object,
  onRowClick: PropTypes.func,
  columnDefinitions: PropTypes.array.isRequired,
  filterTypeConfig: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  idPrefix: PropTypes.string.isRequired,
};

export default Events;
